import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { buildPatientFilters } from '@/utils/patientFilters';

const FILTER_ALL_VALUE = '__all__';

const COMPACT_FIELD_CLASS = 'w-[10.5rem] shrink-0';
const COMPACT_SELECT_TRIGGER_CLASS =
	'h-9 rounded-xl border-stone-200 bg-white px-3 text-xs font-semibold text-zinc-600 shadow-sm';
const COMPACT_LABEL_CLASS =
	'mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-slate-500';

interface CompactSelectFilterProps {
	label: string;
	paramKey: string;
	options: Array<{ value: string; label: string }>;
	value: string;
	onChange: (paramKey: string, value: string) => void;
}

const CompactSelectFilter = ({
	label,
	paramKey,
	options,
	value,
	onChange,
}: CompactSelectFilterProps) => (
	<div className={COMPACT_FIELD_CLASS}>
		<label className={COMPACT_LABEL_CLASS}>{label}</label>
		<Select
			value={value || FILTER_ALL_VALUE}
			onValueChange={(next) => onChange(paramKey, next)}
		>
			<SelectTrigger
				className={COMPACT_SELECT_TRIGGER_CLASS}
				hideTriggerIcon
			>
				<span
					className={cn(
						'truncate',
						!value && 'font-normal text-zinc-400',
					)}
				>
					{value
						? options.find((option) => option.value === value)
								?.label
						: 'Todos'}
				</span>
				<ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0 opacity-50" />
			</SelectTrigger>
			<SelectContent className="bg-white">
				<SelectItem value={FILTER_ALL_VALUE} indicator={false}>
					Todos
				</SelectItem>
				{options.map((option) => (
					<SelectItem
						key={option.value}
						value={option.value}
						indicator={false}
					>
						{option.label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	</div>
);

interface UserAdminFiltersProps {
	clubs: Clubs.Club[];
	companies: Companies.Company[];
}

const FILTER_LABELS: Record<string, string> = {
	banned: 'Status',
	is_complete: 'Cadastro',
	club_id: 'Clube',
	company_id: 'Empresa',
};

const UserAdminFilters = ({ clubs, companies }: UserAdminFiltersProps) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const filterOptions = buildPatientFilters(clubs, companies);

	const handleSimpleFilterChange = useCallback(
		(paramKey: string, value: string) => {
			const nextParams = new URLSearchParams(searchParams.toString());
			if (value === '' || value === FILTER_ALL_VALUE) {
				nextParams.delete(paramKey);
			} else {
				nextParams.set(paramKey, value);
			}
			setSearchParams(nextParams, { replace: true });
		},
		[searchParams, setSearchParams],
	);

	return (
		<div className="flex flex-wrap items-end gap-3">
			{filterOptions.map((filter) => (
				<CompactSelectFilter
					key={filter.type}
					label={FILTER_LABELS[filter.type] ?? filter.label}
					paramKey={filter.type}
					value={searchParams.get(filter.type) ?? ''}
					onChange={handleSimpleFilterChange}
					options={filter.options}
				/>
			))}
		</div>
	);
};

export { UserAdminFilters };
